/**
 * Boîte à outils — écriture des nombres à la française (pur).
 */

/** 2,25 · −0,333 · « — » hors domaine ; au plus `digits` décimales. */
export function formatNumber(v: number, digits = 3): string {
  if (!Number.isFinite(v)) return '—';
  const r = Number(v.toFixed(digits));
  return (Object.is(r, -0) ? 0 : r).toString().replace('.', ',').replace('-', '−');
}

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '-': '⁻',
};

/** Chiffres en exposant : −7 → « ⁻⁷ ». */
export function superscript(n: number): string {
  return String(n)
    .split('')
    .map((c) => SUPERSCRIPT[c] ?? c)
    .join('');
}

/**
 * Écriture scientifique « 2,7 × 10⁸ », « 1 × 10⁻³ » (`digits` décimales de
 * mantisse). La mantisse vient de `toExponential` : jamais « 10 × 10⁻⁴ ».
 */
export function formatSci(v: number, digits = 1): string {
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return '0';
  const [m, e] = v.toExponential(digits).split('e');
  return `${formatNumber(Number(m), digits)} × 10${superscript(Number(e))}`;
}

/** Coordonnées « (2 ; −1,5) ». */
export function formatCoords(x: number, y: number, digits = 2): string {
  return `(${formatNumber(x, digits)} ; ${formatNumber(y, digits)})`;
}

/** Atomes d'un nombre décimal pour la feuille : « −2,5 » → ['−', '2,5']. */
export function numberAtoms(v: number, digits = 3): string[] {
  const text = formatNumber(Math.abs(v), digits);
  const isZero = text === '0';
  return v < 0 && !isZero ? ['−', text] : [text];
}

/** √n exact d'un entier n ≥ 0 : « 5 » si carré parfait, sinon « √29 » (non simplifiée). */
export function exactSqrt(n: number): {
  plain: string;
  tex: string;
  atoms: string[];
  exact: boolean;
} {
  const r = Math.round(Math.sqrt(n));
  if (r * r === n) return { plain: String(r), tex: String(r), atoms: [String(r)], exact: true };
  return { plain: `√${n}`, tex: `\\sqrt{${n}}`, atoms: ['√', String(n)], exact: false };
}

const NNBSP = String.fromCharCode(0x202f);
const NBSP = String.fromCharCode(0xa0);

/**
 * Espaces insécables d'une phrase française produite par un outil : fine
 * avant « : ; ? ! », insécable entre un nombre et son nom (« 3 jetons »,
 * « 2 parts ») et devant un signe isolé (« jetons + ») pour qu'il ne passe
 * pas seul à la ligne.
 */
export function frenchSpaces(s: string): string {
  return s
    .replace(/ ([:;?!])/g, `${NNBSP}$1`)
    .replace(/(\d) (?=(jetons?|paires?|parts?|fois|cases?|groupes?|facteurs?)\b)/g, `$1${NBSP}`)
    .replace(/ ([+−])(?=$|[\s.,;:)])/g, `${NBSP}$1`);
}
